import Vue from "vue";
import Vuex from "vuex";

Vue.use(Vuex);

const showMessage = (state, color, text) => {
  if (!text || (typeof text === "object" && !text.message)) {
    // if object or message null
    // dont show message when no msg
    return;
  }
  state.snackbar.color = color;
  state.snackbar.text = text;
  state.snackbar.date = new Date().getTime().toString(36);
  state.snackbar.show = true;
};

function safeJsonParse(v) {
  if (typeof v !== "string") return undefined;
  try {
    return JSON.parse(v);
  } catch {
    return undefined;
  }
}

const permissionMap = {
  ROLE_RUN_READ: "runReadPermission",
  ROLE_RUN_CREATION: "runCreatePermission",
  ROLE_RUN_DELETION: "runDeletePermission",
  ROLE_RUN_RENAME: "runRenamePermission",
  ROLE_BP_INSTANCE_READ: "bpInstReadPermission",
  ROLE_BP_INSTANCE_CREATION: "bpInstCreatePermission",
  ROLE_BP_INSTANCE_DELETION: "bpInstDeletePermission",
  ROLE_BP_INSTANCE_MODIFY: "bpInstModifyPermission",
  ROLE_BP_INSTANCE_RENAME: "bpInstRenamePermission",
  ROLE_BP_INSTANCE_LOCK_UNLOCK: "bpInstLockPermission",
  ROLE_BP_DEF_READ: "bpDefReadPermission",
  ROLE_BP_DEF_CREATION: "bpDefCreatePermission",
  ROLE_BP_DEF_DELETION: "bpDefDeletePermission",
  ROLE_BP_DEF_MODIFY: "bpDefModifyPermission",
  ROLE_PROCESS_TASK_READ: "processTaskReadPermission",
  ROLE_PROCESS_TASK_CREATION: "processTaskCreatePermission",
  ROLE_PROCESS_TASK_DELETION: "processTaskDeletePermission",
  ROLE_PROCESS_TASK_MODIFY: "processTaskModifyPermission",
  ROLE_SECURITY: "securityPermission",
  ROLE_IMPORT_OLP: "importOlpPermission",
  ROLE_INFO: "infoPermission",
  ROLE_FILE_ENV_READ: "fileEnvReadPermission",
  ROLE_FILE_ENV_CREATION: "fileEnvCreatePermission",
  ROLE_FILE_ENV_DELETION: "fileEnvDeletePermission",
  ROLE_FILE_ENV_MODIFY: "fileEnvModifyPermission",
  ROLE_ENVIRONMENT_PROPS: "envPropsPermission",
  ROLE_RELEASE_DELETION: "releaseDeletePermission",
  ROLE_RUN_OLP_EXPORT: "olpExportPermission",
  ROLE_REPO_ENTITY_CREATION: "repoEntityCreatePermission",
  ROLE_FILE_DELETION: "fileDeletePermission",
  ROLE_LOG: "logPermission",
  ROLE_RETENTION_POLICY: "retentionPolicyPermission",
};

export default new Vuex.Store({
  state: {
    isLoggedIn: localStorage.rememberMe === "true",
    username: localStorage.username,
    locale: localStorage.locale ?? "en",

    runReadPermission: localStorage.runReadPermission === "true",
    runCreatePermission: localStorage.runCreatePermission === "true",
    runDeletePermission: localStorage.runDeletePermission === "true",
    runRenamePermission: localStorage.runRenamePermission === "true",

    bpInstReadPermission: localStorage.bpInstReadPermission === "true",
    bpInstCreatePermission: localStorage.bpInstCreatePermission === "true",
    bpInstDeletePermission: localStorage.bpInstDeletePermission === "true",
    bpInstModifyPermission: localStorage.bpInstModifyPermission === "true",
    bpInstRenamePermission: localStorage.bpInstRenamePermission === "true",
    bpInstLockPermission: localStorage.bpInstLockPermission === "true",

    bpDefReadPermission: localStorage.bpDefReadPermission === "true",
    bpDefCreatePermission: localStorage.bpDefCreatePermission === "true",
    bpDefDeletePermission: localStorage.bpDefDeletePermission === "true",
    bpDefModifyPermission: localStorage.bpDefModifyPermission === "true",

    processTaskReadPermission:
      localStorage.processTaskReadPermission === "true",
    processTaskCreatePermission:
      localStorage.processTaskCreatePermission === "true",
    processTaskDeletePermission:
      localStorage.processTaskDeletePermission === "true",
    processTaskModifyPermission:
      localStorage.processTaskModifyPermission === "true",

    securityPermission: localStorage.securityPermission === "true",

    importOlpPermission: localStorage.importOlpPermission === "true",

    infoPermission: localStorage.infoPermission === "true",

    fileEnvReadPermission: localStorage.fileEnvReadPermission === "true",
    fileEnvCreatePermission: localStorage.fileEnvCreatePermission === "true",
    fileEnvDeletePermission: localStorage.fileEnvDeletePermission === "true",
    fileEnvModifyPermission: localStorage.fileEnvModifyPermission === "true",

    envPropsPermission: localStorage.envPropsPermission === "true",

    releaseDeletePermission: localStorage.releaseDeletePermission === "true",

    olpExportPermission: localStorage.olpExportPermission === "true",

    repoEntityCreatePermission:
      localStorage.repoEntityCreatePermission === "true",
    fileDeletePermission: localStorage.fileDeletePermission === "true",
    logPermission: localStorage.logPermission === "true",
    retentionPolicyPermission:
      localStorage.retentionPolicyPermission === "true",

    snackbar: {
      color: "",
      text: "",
      date: null,
      show: false,
      timeout: 5000,
    },

    numRecentBps: 5,
    recentBps: JSON.parse(localStorage.recentBps ?? "[]"),
    recentBpsMap: JSON.parse(localStorage.recentBpsMap ?? "{}"),

    showPath: localStorage.showPath === "true" ?? false,
    showAll: localStorage.showAll === "true" ?? false,
    treeEnabled: localStorage.treeEnabled === "true" ?? false,
    itemsPerPage: localStorage.itemsPerPage ?? 25,

    comparingFileVersion1: safeJsonParse(localStorage.comparingFileVersion1),
    comparingFileVersion2: safeJsonParse(localStorage.comparingFileVersion2),

    fileComparisonOffset: {
      x: 0,
      y: 0,
    },

    showFileComparison: false,
    keycloak: undefined,

    showPasswordExpiryWarning: false,
    passwordValidityDays: null,
  },

  getters: {
    getStatusIcon: () => (status) => {
      let item = {};
      if (
        status === "COMPUTING" ||
        status === "SCHEDULED" ||
        status === "INITIALIZING" ||
        status === "FINISHING" ||
        status === "FINALIZING"
      ) {
        item.statusIcon = "RUNNING";
      } else if (status === "SUCCESS") {
        item.statusColor = "green";
        item.statusIcon = "mdi-check";
      } else if (
        status === "INIT_ERROR" ||
        status === "ERROR" ||
        status === "REJECTED_DATA"
      ) {
        item.statusColor = "red";
        item.statusIcon = "mdi-close";
      } else if (status === "CANCELLED") {
        item.statusColor = "orange";
        item.statusIcon = "mdi-cancel";
      } else if (
        status === "DEFINING" ||
        status === "IDLE" ||
        status === "WAITING_FOR_CONFIG" ||
        status === "WAITING_FOR_EVALUATION" ||
        status === "WAITING_FOR_DATA" ||
        status === "WAITING_FOR_PROPS" ||
        status === "INVALID_DATA" ||
        status === "INIT"
      ) {
        item.statusColor = "blue";
        item.statusIcon = "mdi-timer-sand";
      } else if (status === "READY_TO_START") {
        item.statusColor = "light-blue";
        item.statusIcon = "mdi-timer-play";
      }
      return item;
    },

    isStatusRunning: () => (status) => {
      return (
        status !== "CANCELLED" &&
        status !== "DEFINING" &&
        status !== "ERROR" &&
        status !== "IDLE" &&
        status !== "INIT_ERROR" &&
        status !== "INIT" &&
        status !== "INVALID_DATA" &&
        status !== "READY_TO_START" &&
        status !== "REJECTED_DATA" &&
        status !== "SUCCESS" &&
        status !== "WAITING_FOR_DATA" &&
        status !== "WAITING_FOR_PROPS" &&
        status !== "WAITING_FOR_EVALUATION" &&
        status !== "WAITING_FOR_CONFIG"
      );
    },

    isStatusPreRun: () => (status) => {
      return (
        status == "INIT" ||
        status == "READY_TO_START" ||
        status == "WAITING_FOR_DATA" ||
        status == "WAITING_FOR_PROPS" ||
        status == "WAITING_FOR_EVALUATION" ||
        status == "REJECTED_DATA" ||
        status == "INVALID_DATA"
      );
    },

    isStatusFinished: () => (status) => {
      return (
        status == "CANCELLED" ||
        status == "SUCCESS" ||
        status == "ERROR" ||
        status == "INIT_ERROR"
      );
    },

    isStatusReady: () => (status) => {
      return status === "READY_TO_START";
    },

    isStatusError: () => (status) => {
      return (
        status === "ERROR" ||
        status === "INIT_ERROR" ||
        status === "INVALID_DATA" ||
        status === "REJECTED_DATA"
      );
    },

    isShowPath: (state) => {
      return state.showPath;
    },

    isShowAll: (state) => {
      return state.showAll;
    },

    isTreeEnabled: (state) => {
      return state.treeEnabled;
    },

    getItemsPerPage: (state) => {
      return state.itemsPerPage;
    },
  },

  mutations: {
    clear(state) {
      localStorage.clear();
      state.isLoggedIn = false;
      state.username = "";

      state.runReadPermission = false;
      state.runCreatePermission = false;
      state.runDeletePermission = false;
      state.runRenamePermission = false;

      state.bpInstReadPermission = false;
      state.bpInstCreatePermission = false;
      state.bpInstDeletePermission = false;
      state.bpInstModifyPermission = false;
      state.bpInstRenamePermission = false;
      state.bpInstLockPermission = false;

      state.bpDefReadPermission = false;
      state.bpDefCreatePermission = false;
      state.bpDefDeletePermission = false;
      state.bpDefModifyPermission = false;

      state.processTaskReadPermission = false;
      state.processTaskCreatePermission = false;

      state.securityPermission = false;

      state.importOlpPermission = false;

      state.infoPermission = false;

      state.fileEnvReadPermission = false;
      state.fileEnvCreatePermission = false;
      state.fileEnvDeletePermission = false;
      state.fileEnvModifyPermission = false;

      state.envPropsPermission = false;

      state.releaseDeletePermission = false;

      state.olpExportPermission = false;

      state.repoEntityCreatePermission = false;
      state.fileDeletePermission = false;
      state.logPermission = false;
      state.retentionPolicyPermission = false;

      state.comparingFileVersion1 = undefined;
      state.comparingFileVersion2 = undefined;

      state.passwordValidityDays = null;
      state.showPasswordExpiryWarning = false;
    },

    setComparingFileVersion1(state, fileVersion) {
      if (!fileVersion) return;
      state.comparingFileVersion1 = fileVersion;
      localStorage.comparingFileVersion1 = JSON.stringify(fileVersion);
    },

    setComparingFileVersion2(state, fileVersion) {
      if (!fileVersion) return;
      state.comparingFileVersion2 = fileVersion;
      localStorage.comparingFileVersion2 = JSON.stringify(fileVersion);
    },

    unsetComparingFileVersion1(state) {
      state.comparingFileVersion1 = undefined;
      localStorage.comparingFileVersion1 = undefined;
    },

    unsetComparingFileVersion2(state) {
      state.comparingFileVersion2 = undefined;
      localStorage.comparingFileVersion2 = undefined;
    },

    addToRecentBps(state, { id, name }) {
      // the "+" makes sure idNumber is a number, this is necessary because id is sometimes a string and sometimes a number, which causes a problem when we call the indexOf method
      const idNumber = +id;
      if (!state.recentBpsMap[idNumber]) {
        if (state.recentBps.length == state.numRecentBps) {
          const lastId = state.recentBps.pop();
          delete state.recentBpsMap[lastId];
        }
      } else {
        state.recentBps.splice(state.recentBps.indexOf(idNumber), 1);
      }
      state.recentBps.unshift(idNumber);
      state.recentBpsMap[idNumber] = name;
      localStorage.recentBps = JSON.stringify(state.recentBps);
      localStorage.recentBpsMap = JSON.stringify(state.recentBpsMap);
    },

    removeFromRecentBps(state, id) {
      // the "+" makes sure idNumber is a number, this is necessary because id is sometimes a string and sometimes a number, which causes a problem when we call the indexOf method
      const idNumber = +id;
      state.recentBps.splice(state.recentBps.indexOf(idNumber), 1);
      delete state.recentBpsMap[idNumber];
      localStorage.recentBps = JSON.stringify(state.recentBps);
      localStorage.recentBpsMap = JSON.stringify(state.recentBpsMap);
    },

    showError(state, error) {
      if (error?.status === 403) {
        // don't show error when the user is not logged in
        return;
      }
      if (error?.message) {
        const errorMessage =
          error.response?.data?.title ?? error.response?.data;

        showMessage(state, "error", error.message + ": " + errorMessage);
      } else if (error?.detail) {
        showMessage(state, "error", error.detail);
      } else if (error?.error) {
        showMessage(state, "error", error.error);
      } else {
        showMessage(state, "error", error);
      }
    },

    showWarning(state, text) {
      showMessage(state, "warning", text);
    },

    showSuccess(state, text) {
      showMessage(state, "success", text);
    },

    setShowPath(state, showPath) {
      localStorage.showPath = showPath;
      state.showPath = showPath;
    },

    setShowAll(state, showAll) {
      localStorage.showAll = showAll;
      state.showAll = showAll;
    },

    setTreeEnabled(state, treeEnabled) {
      localStorage.treeEnabled = treeEnabled;
      state.treeEnabled = treeEnabled;
    },

    setItemsPerPage(state, itemsPerPage) {
      localStorage.itemsPerPage = itemsPerPage;
      state.itemsPerPage = itemsPerPage;
    },

    setFileComparisonPosition(state, { top, left }) {
      state.fileComparisonPosition.top = top;
      state.fileComparisonPosition.left = left;
    },

    showFileComparison(state) {
      state.showFileComparison = true;
    },

    hideFileComparison(state) {
      state.showFileComparison = false;
    },

    setFileComparisonOffset(state, { x, y }) {
      state.fileComparisonOffset.x = x;
      state.fileComparisonOffset.y = y;
    },

    setKeycloak(state, keycloak) {
      state.keycloak = keycloak;
    },

    setLoginDetails(state, { response, username, i18n }) {
      localStorage.username = username;
      state.username = username;

      const locale = localStorage.locale || response.locale;
      localStorage.locale = locale;
      state.locale = locale;
      i18n.locale = locale;

      const permissions = response.permissions;

      for (const [role, key] of Object.entries(permissionMap)) {
        const hasPermission = permissions.includes(role);
        localStorage[key] = hasPermission;
        state[key] = hasPermission;
      }

      localStorage.rememberMe = true;
      state.isLoggedIn = true;
      if (response.passwordValidityDays) {
        state.passwordValidityDays = response.passwordValidityDays;
        const warningThreshold = response.passwordWarningDays || 8;
        state.showPasswordExpiryWarning =
          response.passwordValidityDays < warningThreshold;
      }
    },

    setLocale(state, { locale, i18n }) {
      state.locale = locale;
      localStorage.locale = locale;
      i18n.locale = locale;
    },

    clearPasswordExpiryWarning(state) {
      state.passwordValidityDays = null;
      state.showPasswordExpiryWarning = false;
    },
  },
});
